import { useState, useCallback, useEffect } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { LoadingSpinner } from '../LoadingSpinner';
import { useNotifications } from '../../contexts/NotificationContext';
import { useClickOutside } from '../../hooks/useClickOutside';
import useEscapeKey from '../../hooks/useEscapeKey';
import { motion } from "framer-motion";
import { Conflict, NonStandardField, ImportResolution, ImportResolutionChoice } from '../../types/schema';

interface ImportConflictDialogProps {
  open: boolean;
  onClose: () => void;
  conflictingParameters: Conflict[];
  nonStandardFields: NonStandardField[];
  onResolve: (resolution: ImportResolution) => Promise<void>;
}

const ImportConflictDialog: React.FC<ImportConflictDialogProps> = ({
  open,
  onClose,
  conflictingParameters,
  nonStandardFields,
  onResolve,
}) => {
  const { showNotification } = useNotifications();
  const [isLoading, setIsLoading] = useState(false);
  const [parameterResolutions, setParameterResolutions] = useState<Record<string, ImportResolutionChoice>>({});
  const [nonStandardFieldResolutions, setNonStandardFieldResolutions] = useState<Record<string, ImportResolutionChoice>>({});

  useEffect(() => {
    if (open) {
      setIsLoading(false);
      // Initialize resolutions based on conflicts and non-standard fields
      const initialParamResolutions: Record<string, ImportResolutionChoice> = {};
      conflictingParameters.forEach(conflict => {
        initialParamResolutions[conflict.existingParam.fullPath] = 'overwrite_new'; // Default to overwriting
      });
      setParameterResolutions(initialParamResolutions);

      const initialNonStandardResolutions: Record<string, ImportResolutionChoice> = {};
      nonStandardFields.forEach(field => {
        initialNonStandardResolutions[field.field] = 'ignore'; // Default to ignoring
      });
      setNonStandardFieldResolutions(initialNonStandardResolutions);
    }
  }, [open, conflictingParameters, nonStandardFields]);

  const handleDirectClose = () => {
    onClose();
  };
  
  const dialogRef = useClickOutside<HTMLDivElement>(handleDirectClose);
  useEscapeKey(handleDirectClose);

  const handleParameterResolutionChange = useCallback((fullPath: string, choice: ImportResolutionChoice) => {
    setParameterResolutions(prev => ({ ...prev, [fullPath]: choice }));
  }, []);

  const handleNonStandardFieldResolutionChange = useCallback((field: string, choice: ImportResolutionChoice) => {
    setNonStandardFieldResolutions(prev => ({ ...prev, [field]: choice }));
  }, []);

  const handleGlobalResolution = useCallback(async () => {
    try {
      setIsLoading(true);
      const resolution: ImportResolution = {
        parameterResolutions,
        nonStandardFieldResolutions,
      };
      await onResolve(resolution); 
      onClose();
    } catch (error) {
      console.error('Error resolving import conflict:', error);
      showNotification('error', 'Failed to resolve import conflict.');
    } finally {
      setIsLoading(false);
    }
  }, [onResolve, showNotification, onClose, parameterResolutions, nonStandardFieldResolutions]);

  if (!open) return null;

  return (
    <motion.div
      className="fixed inset-0 bg-gray-950/50 dark:bg-gray-950/70 flex items-center justify-center z-[999]"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <motion.div
        ref={dialogRef}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-[700px] mx-4 border border-gray-200 dark:border-gray-700"
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 20 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
      >
        <div className="px-6 pt-5 rounded-t-lg">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <AlertTriangle size={20} className="text-yellow-500" />
              Import Conflict Detected
            </h2>
            <button
              onClick={handleDirectClose}
              className="text-gray-400 hover:text-gray-300 dark:text-gray-500 dark:hover:text-gray-400"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="p-6 max-h-[60vh] overflow-y-auto">
          <div className="space-y-4">
            <p className="text-gray-700 dark:text-gray-300 text-sm">
              When importing new event schemas, conflicts or non-standard fields were detected.
              Please choose how you would like to resolve each issue:
            </p>

            {conflictingParameters.length > 0 && (
              <>
                <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">Conflicting Properties:</p>
                {conflictingParameters.map((conflict, index) => (
                  <div key={`${conflict.existingParam.fullPath}-${index}`} className="flex flex-col md:flex-row md:items-center md:justify-between p-2 border border-gray-200 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-700">
                    <div className="flex-1 text-sm text-gray-800 dark:text-gray-200">
                        <p className="font-medium">{conflict.existingParam.fullPath}</p>
                        <p className="text-xs text-gray-700 dark:text-gray-300">
                          Existing: <span className="font-mono">{conflict.existingParam.dataTypes.join(', ')}</span>, Imported: <span className="font-mono">{conflict.newParam.dataTypes.join(', ')}</span>
                        </p>
                      </div>
                      <div className="flex items-center space-x-4 mt-2 md:mt-0">
                        <label htmlFor={`param-resolution-${conflict.existingParam.fullPath}-keep-${index}`} className="flex items-center text-sm text-gray-800 dark:text-gray-200">
                          <input
                            type="radio"
                            id={`param-resolution-${conflict.existingParam.fullPath}-keep-${index}`}
                            name={`param-resolution-${conflict.existingParam.fullPath}`}
                            value="keep_existing"
                            checked={parameterResolutions[conflict.existingParam.fullPath] === 'keep_existing'}
                            onChange={() => handleParameterResolutionChange(conflict.existingParam.fullPath, 'keep_existing')}
                            className="form-radio h-4 w-4 text-blue-600 dark:text-blue-400"
                          />
                          <span className="ml-2">Keep Existing</span>
                        </label>
                        <label htmlFor={`param-resolution-${conflict.existingParam.fullPath}-overwrite-${index}`} className="flex items-center text-sm text-gray-800 dark:text-gray-200">
                          <input
                            type="radio"
                            id={`param-resolution-${conflict.existingParam.fullPath}-overwrite-${index}`}
                            name={`param-resolution-${conflict.existingParam.fullPath}`}
                            value="overwrite_new"
                            checked={parameterResolutions[conflict.existingParam.fullPath] === 'overwrite_new'}
                            onChange={() => handleParameterResolutionChange(conflict.existingParam.fullPath, 'overwrite_new')}
                            className="form-radio h-4 w-4 text-green-600 dark:text-green-400"
                          />
                          <span className="ml-2">Overwrite</span>
                        </label>
                      </div>
                    </div>
                  ))}
              </>
            )}

            {nonStandardFields.length > 0 && (
              <div className="space-y-3">
                <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">Non-Standard Fields:</p>
                {nonStandardFields.map((field, index) => (
                  <div key={`${field.field}-${field.path}-${index}`} className="flex flex-col md:flex-row md:items-center md:justify-between p-2 border border-gray-200 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-700">
                    <div className="flex-1 text-sm text-gray-800 dark:text-gray-200">
                      <p className="font-medium">{field.field}</p>
                      <p className="text-xs text-gray-700 dark:text-gray-300">
                        Found in: <span className="font-mono">{field.path}</span>
                      </p>
                    </div>
                    <div className="flex items-center space-x-4 mt-2 md:mt-0">
                      <label htmlFor={`non-standard-resolution-${field.field}-${field.path}-ignore`} className="flex items-center text-sm text-gray-800 dark:text-gray-200">
                        <input
                          type="radio"
                          id={`non-standard-resolution-${field.field}-${field.path}-ignore`}
                          name={`non-standard-resolution-${field.field}-${field.path}`}
                          value="ignore"
                          checked={nonStandardFieldResolutions[field.field] === 'ignore'}
                          onChange={() => handleNonStandardFieldResolutionChange(field.field, 'ignore')}
                          className="form-radio h-4 w-4 text-blue-600 dark:text-blue-400"
                        />
                        <span className="ml-2">Ignore</span>
                      </label>
                      <label htmlFor={`non-standard-resolution-${field.field}-${field.path}-keep`} className="flex items-center text-sm text-gray-800 dark:text-gray-200">
                        <input
                          type="radio"
                          id={`non-standard-resolution-${field.field}-${field.path}-keep`}
                          name={`non-standard-resolution-${field.field}-${field.path}`}
                          value="keep_existing" // Using keep_existing to mean "keep" for non-standard fields
                          checked={nonStandardFieldResolutions[field.field] === 'keep_existing'}
                          onChange={() => handleNonStandardFieldResolutionChange(field.field, 'keep_existing')}
                          className="form-radio h-4 w-4 text-green-600 dark:text-green-400"
                        />
                        <span className="ml-2">Keep</span>
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="px-6 py-4 rounded-b-lg border-t border-gray-200 dark:border-gray-800">
          <div className="flex justify-end gap-3">
            <button
              onClick={handleDirectClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800 dark:text-gray-300 dark:hover:text-gray-200"
            >
              Cancel
            </button>
            <button
              onClick={handleGlobalResolution}
              disabled={isLoading}
              className={`px-4 py-2 text-sm font-medium rounded-md
                ${isLoading ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}
              `}
            >
              {isLoading ? <LoadingSpinner size="sm" className="inline mr-2" /> : null}
              Apply Resolutions
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ImportConflictDialog;
